/**
 * PAY.JP payjp.js v2 ローダー
 *
 * - `https://js.pay.jp/v2/pay.js` は「必要になったときに 1 回だけ」動的に読み込む。
 *   全ページで読み込むと DGFT 店舗でも外部スクリプトを取りに行ってしまうため、
 *   nuxt.config.js の head.script には入れない。
 * - `payjp_pk` は店舗ごとに異なる（CMP は HQ モードで他店舗顧客を扱いうる）ため、
 *   単一インスタンスではなく「pk を受け取って Payjp インスタンスを返すファクトリ」を inject する。
 * - payjp.js は同じ pk で `Payjp()` を複数回呼ぶと警告を出すので、pk + options 単位でキャッシュする。
 *
 * 3DS を足すときは呼び出し側で `$payjpFactory(pk, { threeDSecureWorkflow: 'iframe' })` を
 * 渡せばよい（設計書 docs/plans/2026-09-19-payjp-frontend-phase2-design.md §5.3）。
 */
export const PAYJP_SCRIPT_URL = 'https://js.pay.jp/v2/pay.js'
export const PAYJP_SCRIPT_ID = 'payjp-js-v2'

// script の読み込み Promise（アプリ全体で 1 つ）
let scriptPromise = null
// `${pk}::${JSON.stringify(options)}` -> Payjp インスタンス
const instanceCache = new Map()

/**
 * payjp.js v2 を 1 回だけ読み込む
 * @returns {Promise<Function>} window.Payjp
 */
export function loadPayjpScript() {
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('payjp.js はブラウザでのみ読み込めます'))
      return
    }
    if (window.Payjp) {
      resolve(window.Payjp)
      return
    }

    // 他の経路で既に挿入済みなら load を待つ
    let script = document.getElementById(PAYJP_SCRIPT_ID)
    if (!script) {
      script = document.createElement('script')
      script.id = PAYJP_SCRIPT_ID
      script.src = PAYJP_SCRIPT_URL
      script.async = true
      document.head.appendChild(script)
    }

    script.addEventListener('load', () => {
      if (window.Payjp) {
        resolve(window.Payjp)
      } else {
        scriptPromise = null
        reject(new Error('payjp.js の読み込みに失敗しました'))
      }
    })
    script.addEventListener('error', () => {
      // 次回リトライできるようにキャッシュを捨てる
      scriptPromise = null
      if (script.parentNode) script.parentNode.removeChild(script)
      reject(new Error('payjp.js の読み込みに失敗しました'))
    })
  })

  return scriptPromise
}

/**
 * pk（＋options）ごとに Payjp インスタンスを作って返す
 * @param {string} publicKey 店舗の payjp_pk
 * @param {Object} options Payjp() の第2引数（3DS を足すときはここに threeDSecureWorkflow）
 * @returns {Promise<Object>} Payjp インスタンス
 */
export async function createPayjp(publicKey, options = {}) {
  if (!publicKey) throw new Error('PAY.JP の公開鍵が設定されていません')

  const cacheKey = `${publicKey}::${JSON.stringify(options)}`
  if (instanceCache.has(cacheKey)) return instanceCache.get(cacheKey)

  const Payjp = await loadPayjpScript()
  const instance = Payjp(publicKey, options)
  instanceCache.set(cacheKey, instance)
  return instance
}

// テスト用（本番コードからは呼ばない）
export function _resetPayjpCache() {
  scriptPromise = null
  instanceCache.clear()
}

export default (ctx, inject) => {
  inject('payjpFactory', createPayjp)
}
